import React, { useState, useEffect, useMemo } from "react";
import { Card, Table, Button, Tag, Space, message, Modal, Typography, Tabs, Popconfirm } from "antd";
import { DownloadOutlined, FileTextOutlined, FileExcelOutlined, EyeOutlined, ImportOutlined, CheckCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { useGetFiles } from "~/services/file/file.queries";
import { DocumentType } from "~/utils/constants";
import { TFile } from "~/types";
import { useQueryClient } from "@tanstack/react-query";
import { inventoryService } from "~/services/inventory/inventory.services";
import { INVENTORY_KEYS } from "~/services/inventory/keys";
import { CsvImportPreviewModal } from "./csv-import-preview-modal";
import { PdfImportModal } from "../../admin/dispatch/inventory-control/PdfImportModal";
import { useDeleteFile } from "~/services/file/file.mutations";
import dayjs from "dayjs";

const COMPLETED_IMPORTS_STORAGE_KEY = "completed_inventory_imports";

const { Text } = Typography;

export const PendingImportFiles: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<TFile | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [csvPreviewVisible, setCsvPreviewVisible] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [pdfImportVisible, setPdfImportVisible] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState<"pending" | "completed">("pending");
  const [completedFileIds, setCompletedFileIds] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const deleteFileMutation = useDeleteFile();

  const [completedFileData, setCompletedFileData] = useState<Record<string, string>>({});

  // Load completed file IDs from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(COMPLETED_IMPORTS_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        // Filter to only keep IDs from last 30 days
        const thirtyDaysAgo = dayjs().subtract(30, 'days');
        const recent: Record<string, string> = {};
        Object.entries(data).forEach(([id, timestamp]: [string, any]) => {
          if (dayjs(timestamp).isAfter(thirtyDaysAgo)) {
            recent[id] = timestamp;
          }
        });
        setCompletedFileData(recent);
        setCompletedFileIds(new Set(Object.keys(recent)));
      }
    } catch (error) {
      console.error("Error loading completed imports:", error);
    }
  }, []);

  // Only show files from the last 30 days
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const { data: filesData, isLoading, refetch } = useGetFiles(
    { type: DocumentType.INVENTORY, page: 1, pageSize: 50 },
    true
  );

  // Filter files to only show recent ones (last 30 days) and only files with valid size
  const allFiles = useMemo(() => {
    return (filesData?.items || []).filter((file) => {
      if (!file.createdAt) return false;
      const fileDate = new Date(file.createdAt);
      const isRecent = fileDate >= thirtyDaysAgo;
      
      // Only include files with a valid size (not empty, not null, and is a valid number)
      const hasValidSize = file.size && 
                           file.size.trim() !== "" && 
                           !isNaN(parseInt(file.size)) && 
                           parseInt(file.size) > 0;
      
      return isRecent && hasValidSize;
    });
  }, [filesData?.items, thirtyDaysAgo]);

  // Separate pending and completed files
  const pendingFiles = useMemo(() => {
    return allFiles.filter((file) => !completedFileIds.has(file.id));
  }, [allFiles, completedFileIds]);

  const completedFiles = useMemo(() => {
    return allFiles.filter((file) => completedFileIds.has(file.id));
  }, [allFiles, completedFileIds]);

  // Mark a file as completed
  const markFileAsCompleted = (fileId: string) => {
    try {
      const stored = localStorage.getItem(COMPLETED_IMPORTS_STORAGE_KEY);
      const data = stored ? JSON.parse(stored) : {};
      const timestamp = new Date().toISOString();
      data[fileId] = timestamp;
      localStorage.setItem(COMPLETED_IMPORTS_STORAGE_KEY, JSON.stringify(data));
      setCompletedFileData({ ...completedFileData, [fileId]: timestamp });
      setCompletedFileIds(new Set([...completedFileIds, fileId]));
    } catch (error) {
      console.error("Error marking file as completed:", error);
    }
  };

  const handleDownload = (file: TFile) => {
    if (file.filePath) {
      window.open(file.filePath, "_blank");
    } else {
      message.error("File path not available");
    }
  };

  const handlePreview = (file: TFile) => {
    setSelectedFile(file);
    setPreviewVisible(true);
  };

  const handleDelete = async (file: TFile) => {
    try {
      await deleteFileMutation.mutateAsync(file.id);
      message.success("File deleted successfully");
      refetch();
    } catch (error: any) {
      console.error("Error deleting file:", error);
      message.error(error.response?.data?.message || "Failed to delete file");
    }
  };

  const handleImport = async (file: TFile) => {
    if (!file.filePath) {
      message.error("File path not available");
      return;
    }

    // For CSV files, open the preview/edit modal
    if (file.fileName?.toLowerCase().endsWith(".csv")) {
      try {
        // Fetch the file and create a File object
        const response = await fetch(file.filePath);
        if (!response.ok) {
          throw new Error("Failed to fetch file");
        }
        const blob = await response.blob();
        const fileObj = new File([blob], file.fileName || "inventory.csv", { type: "text/csv" });
        
        setCsvFile(fileObj);
        setCsvPreviewVisible(true);
        setSelectedFile(file);
      } catch (error: any) {
        console.error("Error loading file:", error);
        message.error(error.message || "Failed to load file for preview");
      }
    } else if (file.fileName?.toLowerCase().endsWith(".pdf")) {
      // For PDF files, fetch and open the PDF import modal
      try {
        const response = await fetch(file.filePath);
        if (!response.ok) {
          throw new Error("Failed to fetch file");
        }
        const blob = await response.blob();
        const fileObj = new File([blob], file.fileName || "inventory.pdf", { type: "application/pdf" });
        
        // Get client ID from the file record if available
        const clientId = (file as any).client?.id;
        
        setPdfFile(fileObj);
        setPdfImportVisible(true);
        setSelectedFile(file);
      } catch (error: any) {
        console.error("Error loading PDF file:", error);
        message.error(error.message || "Failed to load PDF file");
      }
    } else {
      message.warning("Unsupported file type. Please download and review manually.");
    }
  };

  const columns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      render: (fileName: string, record: TFile) => {
        // Use fileName, title, or fallback - avoid showing internal IDs
        const displayName = fileName || record.title || "Untitled";
        // If it looks like an internal ID (starts with "file-" and has numbers), try to use title instead
        const isInternalId = displayName.match(/^file-\d+$/i);
        const finalName = isInternalId && record.title ? record.title : displayName;
        
        return (
          <div className="flex items-center gap-2">
            {finalName?.toLowerCase().endsWith(".pdf") || fileName?.toLowerCase().endsWith(".pdf") ? (
              <FileTextOutlined className="text-red-500" />
            ) : (
              <FileExcelOutlined className="text-green-500" />
            )}
            <span>{finalName}</span>
          </div>
        );
      },
    },
    {
      title: "Client",
      key: "client",
      render: (_: any, record: TFile) => {
        // Try multiple ways to access the client
        const client = record.client || (record as any).client;
        
        if (!client) {
          // If client relation is missing, show clientId if available for debugging
          const clientId = (record as any).clientId;
          if (clientId) {
            return `Client ID: ${clientId.substring(0, 8)}...`;
          }
          return "-";
        }
        
        // Display client name
        const companyName = client.companyName || client.company_name;
        const firstName = client.firstName || client.first_name;
        const lastName = client.lastName || client.last_name;
        
        if (companyName) {
          return companyName;
        }
        
        const fullName = `${firstName || ""} ${lastName || ""}`.trim();
        return fullName || "Unknown";
      },
    },
    {
      title: "Uploaded",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => dayjs(date).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      render: (size: string) => {
        if (!size) return "-";
        const bytes = parseInt(size);
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: TFile) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
            size="small"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
            size="small"
          >
            Download
          </Button>
          <Button
            type="link"
            icon={<ImportOutlined />}
            onClick={() => handleImport(record)}
            size="small"
          >
            Import
          </Button>
          <Popconfirm
            title="Delete file"
            description="Are you sure you want to delete this file?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={deleteFileMutation.isPending}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Completed columns - defined after columns to avoid initialization error
  const completedColumns = useMemo(() => [
    ...columns.slice(0, -1), // All columns except Actions
    {
      title: "Completed",
      key: "completedAt",
      render: (_: any, record: TFile) => {
        const completedAt = completedFileData[record.id];
        return completedAt ? dayjs(completedAt).format("YYYY-MM-DD HH:mm") : "-";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: TFile) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
            size="small"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
            size="small"
          >
            Download
          </Button>
          <Popconfirm
            title="Delete file"
            description="Are you sure you want to delete this file?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={deleteFileMutation.isPending}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], [columns, completedFileData, handlePreview, handleDownload, handleDelete, deleteFileMutation.isPending]);

  return (
    <>
      <div className="mb-4">
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as "pending" | "completed")}
          items={[
            {
              key: "pending",
              label: (
                <span>
                  Pending Imports
                  {pendingFiles.length > 0 && (
                    <Tag color="orange" className="ml-2">
                      {pendingFiles.length}
                    </Tag>
                  )}
                </span>
              ),
              children: (
                <div>
                  {pendingFiles.length === 0 && !isLoading ? (
                    <div className="text-center py-8 text-gray-500">
                      No pending import files from the last 30 days
                    </div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={pendingFiles}
                      loading={isLoading}
                      rowKey="id"
                      pagination={false}
                      size="small"
                    />
                  )}
                </div>
              ),
            },
            {
              key: "completed",
              label: (
                <span>
                  Completed Imports
                  {completedFiles.length > 0 && (
                    <Tag color="green" className="ml-2">
                      {completedFiles.length}
                    </Tag>
                  )}
                </span>
              ),
              children: (
                <div>
                  {completedFiles.length === 0 && !isLoading ? (
                    <div className="text-center py-8 text-gray-500">
                      No completed imports from the last 30 days
                    </div>
                  ) : (
                    <Table
                      columns={completedColumns}
                      dataSource={completedFiles}
                      loading={isLoading}
                      rowKey="id"
                      pagination={false}
                      size="small"
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>

      <Modal
        title={`File Preview: ${selectedFile?.fileName || selectedFile?.title || "Untitled"}`}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="download" icon={<DownloadOutlined />} onClick={() => selectedFile && handleDownload(selectedFile)}>
            Download
          </Button>,
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedFile && (
          <div className="space-y-4">
            <div>
              <Text strong>Client: </Text>
              <Text>
                {(selectedFile as any).client?.companyName ||
                  `${(selectedFile as any).client?.firstName || ""} ${(selectedFile as any).client?.lastName || ""}`.trim() ||
                  "Unknown"}
              </Text>
            </div>
            <div>
              <Text strong>Uploaded: </Text>
              <Text>{dayjs(selectedFile.createdAt).format("YYYY-MM-DD HH:mm")}</Text>
            </div>
            <div>
              <Text strong>File Size: </Text>
              <Text>
                {selectedFile.size
                  ? (() => {
                      const bytes = parseInt(selectedFile.size);
                      if (bytes < 1024) return `${bytes} B`;
                      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                    })()
                  : "-"}
              </Text>
            </div>
            {selectedFile.filePath && (
              <div>
                <Text strong>Preview: </Text>
                <div className="mt-2">
                  {selectedFile.fileName?.toLowerCase().endsWith(".pdf") ? (
                    <iframe
                      src={selectedFile.filePath}
                      className="w-full h-96 border rounded"
                      title="PDF Preview"
                    />
                  ) : (
                    <div className="p-4 bg-gray-50 border rounded">
                      <Text type="secondary">
                        CSV files cannot be previewed inline. Please download to view.
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* CSV Import Preview - Inline */}
      <CsvImportPreviewModal
        open={csvPreviewVisible}
        file={csvFile}
        clientId={(selectedFile as any)?.client?.id}
        inline
        onClose={() => {
          setCsvPreviewVisible(false);
          setCsvFile(null);
          setSelectedFile(null);
        }}
        onSuccess={() => {
          refetch();
          // Mark the file as completed when import succeeds
          if (selectedFile?.id) {
            markFileAsCompleted(selectedFile.id);
          }
        }}
      />

      {/* PDF Import - Inline */}
      <PdfImportModal
        open={pdfImportVisible}
        initialFile={pdfFile || undefined}
        initialClientId={(selectedFile as any)?.client?.id}
        inline
        onClose={() => {
          setPdfImportVisible(false);
          setPdfFile(null);
          setSelectedFile(null);
        }}
        onSuccess={() => {
          refetch();
          // Mark the file as completed when import succeeds
          if (selectedFile?.id) {
            markFileAsCompleted(selectedFile.id);
          }
        }}
      />
    </>
  );
};

